import { useState, useEffect } from 'react';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNotification } from '../../hooks/useNotification';
import './NotificationPrompt.scss';

export interface NotificationPromptProps {
  onClose?: () => void;
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

export function NotificationPrompt({
  onClose,
  onPermissionGranted,
  onPermissionDenied,
}: NotificationPromptProps) {
  const { permission, isSupported, requestPermission } = useNotification();
  const [isVisible, setIsVisible] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    // Show prompt only if supported and permission is default
    if (isSupported && permission === 'default') {
      // Delay showing the prompt to avoid being intrusive
      const timer = setTimeout(() => {
        const dismissed = localStorage.getItem('notification-prompt-dismissed');
        if (!dismissed) {
          setIsVisible(true);
        }
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [isSupported, permission]);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const newPermission = await requestPermission();

      if (newPermission === 'granted') {
        onPermissionGranted?.();
        setIsVisible(false);
      } else if (newPermission === 'denied') {
        onPermissionDenied?.();
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('notification-prompt-dismissed', 'true');
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible || !isSupported || permission !== 'default') {
    return null;
  }

  return (
    <div className="notification-prompt-overlay">
      <div className="notification-prompt">
        <div className="notification-prompt-header">
          <BellAlertIcon className="notification-prompt-icon" />
          <button
            className="notification-prompt-close"
            onClick={handleDismiss}
            aria-label="Close notification prompt"
          >
            <XMarkIcon />
          </button>
        </div>

        <div className="notification-prompt-content">
          <h3 className="notification-prompt-title">Aktifkan Notifikasi</h3>
          <p className="notification-prompt-description">
            Dapatkan notifikasi tentang program donasi terbaru dan update dari LAZISMU UMMI
          </p>
        </div>

        <div className="notification-prompt-actions">
          <button
            className="notification-prompt-button notification-prompt-button--secondary"
            onClick={handleDismiss}
            disabled={isRequesting}
          >
            Nanti Saja
          </button>
          <button
            className="notification-prompt-button notification-prompt-button--primary"
            onClick={handleRequestPermission}
            disabled={isRequesting}
          >
            {isRequesting ? 'Memproses...' : 'Aktifkan'}
          </button>
        </div>
      </div>
    </div>
  );
}
